//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Config imports
import { API_URL, getAuthHeaders } from "@/config/api";

//* Utils imports
import { QUERY_KEYS } from "@/utils/query-client";
import { parseMongoData } from "@/utils/mongoUtils";

//* Types imports
import type { ISaleOutputDTO, ISaleFilters } from "@/schemas/sales";
import type { Client } from "@/schemas/client";
import type { IProduct } from "@/schemas/product";
import type { User } from "@/schemas/user";

const processSale = (sale: any): ISaleOutputDTO => ({
  ...sale,
  _id: sale._id?.$oid || sale._id || sale.id || "",
  clientId: parseMongoData<Client>(sale.clientId),
  productId: parseMongoData<IProduct>(sale.productId),
  responsibleId: parseMongoData<User>(sale.responsibleId),
});

async function indexSales(
  filters: ISaleFilters = {},
): Promise<ISaleOutputDTO[]> {
  const queryParams = new URLSearchParams();
  if (filters.paymentStatus) {
    if (Array.isArray(filters.paymentStatus)) {
      filters.paymentStatus.forEach((status) =>
        queryParams.append("paymentStatus", status),
      );
    } else {
      queryParams.append("paymentStatus", filters.paymentStatus);
    }
  }
  if (filters.startDate) queryParams.append("startDate", filters.startDate);
  if (filters.endDate) queryParams.append("endDate", filters.endDate);

  const response = await fetch(`${API_URL}/sales?${queryParams.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch sales");
  }

  const result = await response.json();
  const rawData = Array.isArray(result) ? result : (result as any).data;

  return (rawData || []).map(processSale);
}

export function useIndexSales(filters?: ISaleFilters) {
  return useQuery<ISaleOutputDTO[]>({
    queryKey: [QUERY_KEYS.SALES.ALL, filters],
    queryFn: () => indexSales(filters),
  });
}
